import React, { useEffect, useMemo, useState } from 'react';
import { X as ClearOutlinedIcon, Search } from 'lucide-react';

import { useAppDispatch, useAppSelector } from 'src/store';
import { NewProjectDefinitionFormState, setFormState } from 'src/app/projects/views/new-project-definition/state/newProjectDefinitionSlice';
import { getCurrentProjectDefinitionFormState } from 'src/app/projects/views/new-project-definition/state/newProjectDefinitionSelector';

import { CMSHabitatAssayType } from 'src/shared/types';
import cmsService from 'src/services/cms-service';
import Autocomplete from 'src/shared/components/autocomplete/Autocomplete';
import TestTypeChip from 'src/shared/components/test-type-chip/TestTypeChip';

import commonStyles from 'src/app/projects/views/new-project-definition/NewProjectDefinition.module.scss';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import styles from './HabitatAssayTypes.module.scss';

const HabitatAssayTypes = () => {
    const dispatch = useAppDispatch();
    const formState = useAppSelector(getCurrentProjectDefinitionFormState);
    const [habitatAssayOptions, setHabitatAssayOptions] = useState<CMSHabitatAssayType[]>([]);
    const { habitatAssayTypes } = formState;
    const [habitatAssaySearchText, setHabitatAssaySearchText] = useState('');
    const [selectedValue, setSelectedValue] = useState<string>('');

    useEffect(() => {
        const fetchHabitatAssayTypes = async () => {
            const allHabitatAssays = await cmsService.getAllHabitatAssayTypes();

            setHabitatAssayOptions(allHabitatAssays as CMSHabitatAssayType[]);
        };

        fetchHabitatAssayTypes();
    }, []);

    const autocompleteOptions = useMemo(() => {
        // Sort the options by habitat name and then by assay name
        const sortedOptions = [...habitatAssayOptions].sort((a, b) => {
            const habitatComparison = a.habitatName.localeCompare(b.habitatName);
            if (habitatComparison !== 0) {
                return habitatComparison;
            }
            return a.assayName.localeCompare(b.assayName);
        });
        return ['', ...sortedOptions.map(entry => `${entry.habitatName} - ${entry.assayName} (${entry.habitatAssayKey})`)];
    }, [habitatAssayOptions]);

    // Check if selected value is a duplicate (must be before early return)
    const isDuplicate = useMemo(() => {
        if (!selectedValue || !habitatAssayOptions.length) {
            return false;
        }
        const selectedType = habitatAssayOptions.find(
            entry => `${entry.habitatName} - ${entry.assayName} (${entry.habitatAssayKey})` === selectedValue
        );
        if (!selectedType) {
            return false;
        }
        return habitatAssayTypes.some(entry => entry.habitatAssayKey === selectedType.habitatAssayKey);
    }, [selectedValue, habitatAssayOptions, habitatAssayTypes]);

    if (!habitatAssayOptions.length) {
        return null;
    }

    const onFormChange = (fragment: Partial<NewProjectDefinitionFormState>) => {
        dispatch(
            setFormState({
                ...fragment,
            })
        );
    };

    const removeHabitatAssayType = (habitatAssayKey: string) => {
        onFormChange({
            habitatAssayTypes: [...habitatAssayTypes].filter(item => item.habitatAssayKey !== habitatAssayKey),
        });
    };

    return (
        <div className={commonStyles.formSection}>
            <div className={commonStyles.formInputSection}>
                <div className={commonStyles.formInputSectionTitle}>
                    Test <QuestionButtonHelp type='api' slug='project-definition-habitat-test-type' />
                </div>
                <div className={styles.addHabitatAssayType}>
                    <div className={styles.autocompleteWrapper}>
                        <Autocomplete
                            options={autocompleteOptions}
                            placeholder='Search'
                            prefixIcon={<Search />}
                            onChange={value => {
                                if (!value) {
                                    setSelectedValue('');
                                    return;
                                }

                                const selectedType = habitatAssayOptions.find(
                                    entry => `${entry.habitatName} - ${entry.assayName} (${entry.habitatAssayKey})` === value
                                );

                                if (selectedType) {
                                    // Check if maximum limit reached
                                    if (habitatAssayTypes.length >= 8) {
                                        setSelectedValue('');
                                        setHabitatAssaySearchText('');
                                        return;
                                    }

                                    // Check if it's already added (duplicate detection)
                                    if (habitatAssayTypes.some(entry => entry.habitatAssayKey === selectedType.habitatAssayKey)) {
                                        setSelectedValue(value);
                                        setHabitatAssaySearchText('');
                                        return;
                                    }

                                    onFormChange({
                                        habitatAssayTypes: [
                                            ...habitatAssayTypes,
                                            {
                                                habitatAssayKey: selectedType.habitatAssayKey,
                                                habitatName: selectedType.habitatName,
                                                assayName: selectedType.assayName,
                                            },
                                        ],
                                    });
                                    // Clear the input immediately after adding
                                    setSelectedValue('');
                                    setHabitatAssaySearchText('');
                                } else {
                                    setSelectedValue('');
                                }
                            }}
                            onInputChange={value => {
                                setHabitatAssaySearchText(value);
                                setSelectedValue('');
                            }}
                            inputValue={habitatAssaySearchText}
                            value={selectedValue}
                            updateInputOnSelect={false}
                            disabled={habitatAssayTypes.length >= 8}
                        />
                        {isDuplicate && <span className={styles.duplicateHint}>This test type already exists</span>}
                    </div>
                </div>
                <div className={styles.habitatAssayTypes}>
                    {habitatAssayTypes.map((entry, index) => {
                        return (
                            <div key={index} className={styles.habitatAssayTypeTag}>
                                <TestTypeChip testTypeKey={entry.habitatAssayKey} />
                                <ClearOutlinedIcon onClick={() => removeHabitatAssayType(entry.habitatAssayKey)} />
                            </div>
                        );
                    })}
                </div>
                {habitatAssayTypes.length > 0 && <div className={commonStyles.maximumText}>{habitatAssayTypes.length}/8 tests added</div>}
            </div>
        </div>
    );
};

export default HabitatAssayTypes;
